import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useAnnotateKsPackageIssueMutation,
  useBuildKsPackageMutation,
  useCreateKsPackageMutation,
  useCreateSmetaRequestMutation,
  useDeleteKsPackageMutation,
  useGetKsPackageQuery,
  useImportKsPackageDocsFromMtoMutation,
  useImportKsPackageTripsFromLogistMutation,
  useIssueKsPackageMutation,
  useListContractEstimateOptionsQuery,
  useListContractLsrQuery,
  useListKsPackageDocsQuery,
  useListKsPackageFormsQuery,
  useListKsPackageIssuesQuery,
  useListKsPackageLinesQuery,
  useListKsPackageMaterialsQuery,
  useListKsPackagesQuery,
  useListSmetaContextOptionsQuery,
  useMeQuery,
  useRenderKsPackageActFormsMutation,
  useRenderKsPackageFormsMutation,
  useUploadKsPackageDocsMutation,
  useUpsertKsPackageActMutation,
  useUpsertKsPackageOverrideMutation,
  type KsPackageIssue,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { useAppSelector } from '@app/hooks';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import {
  ACT_FORM_CODES,
  PACKAGE_RELEASED,
  PACKAGE_RUNNING,
} from '@shared/lib/smetaLabels';
import type { StepKey } from '../../KsPackageHeader';
import { isKsPackagesDisabled } from '../helpers';
import { useKsPackageActions } from './useKsPackageActions';
import { useRegistryPage, useSelectedRecordParam } from '../../registryPaging';

export function useKsPackageState() {
  const { can_write } = useAgentPermission('smeta');
  const { activeProjectId, setActiveProject } = useActiveProjectContext();

  const [createError, setCreateError] = useState<string | null>(null);
  const [actError, setActError] = useState<string | null>(null);
  const [docError, setDocError] = useState<string | null>(null);

  // Детализация
  const [selectedId, setSelectedId] = useSelectedRecordParam();
  const [step, setStep] = useState<StepKey>('setup');
  const [actFilter, setActFilter] = useState('');
  const [poll, setPoll] = useState(0);
  const [renderWatchUntil, setRenderWatchUntil] = useState(0);
  const [renderQueuedAt, setRenderQueuedAt] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const buildStampRef = useRef<string | null>(null);

  const [formActId, setFormActId] = useState('');
  const [formCode, setFormCode] = useState<string>(ACT_FORM_CODES[0]);

  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const currentUserId = useAppSelector((s) => s.auth.user?.id);
  const meRoles = useMeQuery().data?.roles ?? [];

  const optionsQ = useListContractEstimateOptionsQuery();
  const contextOptionsQ = useListSmetaContextOptionsQuery();
  const packagesPage = useRegistryPage();
  const listQ = useListKsPackagesQuery(
    { limit: packagesPage.limit, offset: packagesPage.offset },
    { pollingInterval: poll },
  );
  const detailQ = useGetKsPackageQuery(selectedId ?? '', {
    skip: !selectedId,
    pollingInterval: poll,
  });

  const pkgContractId = detailQ.data?.package.contract_id ?? '';
  const contractLsrQ = useListContractLsrQuery(pkgContractId, { skip: !pkgContractId });

  const linesQ = useListKsPackageLinesQuery(
    { packageId: selectedId ?? '', actId: actFilter || undefined },
    { skip: !selectedId, pollingInterval: poll },
  );
  const issuesQ = useListKsPackageIssuesQuery(
    { packageId: selectedId ?? '' },
    { skip: !selectedId, pollingInterval: poll },
  );
  const materialsQ = useListKsPackageMaterialsQuery(
    { packageId: selectedId ?? '', actId: actFilter || undefined },
    { skip: !selectedId, pollingInterval: poll },
  );
  const allLinesQ = useListKsPackageLinesQuery(
    { packageId: selectedId ?? '' },
    { skip: !selectedId },
  );
  const allMaterialsQ = useListKsPackageMaterialsQuery(
    { packageId: selectedId ?? '' },
    { skip: !selectedId },
  );
  const docsQ = useListKsPackageDocsQuery({ packageId: selectedId ?? '' }, { skip: !selectedId });
  const formsQ = useListKsPackageFormsQuery(
    { packageId: selectedId ?? '' },
    { skip: !selectedId, pollingInterval: poll },
  );

  const [createPackage, { isLoading: creating }] = useCreateKsPackageMutation();
  const [deletePackage] = useDeleteKsPackageMutation();
  const [buildPackage, { isLoading: building }] = useBuildKsPackageMutation();
  const [issuePackage, { isLoading: issuing }] = useIssueKsPackageMutation();
  const [createRequest, { isLoading: sendingRequest }] = useCreateSmetaRequestMutation();
  const [importDocsFromMto, { isLoading: importingMto }] = useImportKsPackageDocsFromMtoMutation();
  const [importTripsFromLogist, { isLoading: importingLogist }] =
    useImportKsPackageTripsFromLogistMutation();
  const [upsertAct, { isLoading: savingAct }] = useUpsertKsPackageActMutation();
  const [annotate, { isLoading: annotating }] = useAnnotateKsPackageIssueMutation();
  const [uploadDocs, { isLoading: uploadingDocs }] = useUploadKsPackageDocsMutation();
  const [renderForms, { isLoading: rendering }] = useRenderKsPackageFormsMutation();
  const [renderActForms, { isLoading: renderingAct }] = useRenderKsPackageActFormsMutation();
  const [upsertOverride] = useUpsertKsPackageOverrideMutation();

  const disabled = isKsPackagesDisabled(listQ.error);
  const packages = listQ.data?.items ?? [];
  const detail = detailQ.data;
  const pkg = detail?.package;
  const acts = detail?.acts ?? [];
  const lines = linesQ.data?.items ?? [];
  const allLines = allLinesQ.data?.items ?? [];
  const issues = issuesQ.data?.items ?? [];
  const materials = materialsQ.data?.items ?? [];
  const materialsTotal = allMaterialsQ.data?.items.length ?? 0;
  const materialSummary = materialsQ.data?.summary ?? [];
  const docs = docsQ.data?.items ?? [];
  const forms = formsQ.data?.items ?? [];
  const counts = issuesQ.data?.counts;
  const contractLsrs = contractLsrQ.data?.items ?? [];

  const frozen = Boolean(pkg && PACKAGE_RELEASED.has(pkg.status));
  const busy = Boolean(pkg && PACKAGE_RUNNING.has(pkg.status));
  // Удалить пакет может автор, администратор или директор — так же решает
  // сервер (П11 п.5). Пакет без автора удаляют только администратор и директор.
  const canDelete = Boolean(
    (pkg?.created_by && currentUserId && pkg.created_by === currentUserId) ||
      meRoles.includes('admin') ||
      meRoles.includes('director'),
  );

  const issuesByLine = useMemo(() => {
    const map = new Map<string, KsPackageIssue[]>();
    for (const i of issues) {
      if (!i.line_id) continue;
      const list = map.get(i.line_id);
      if (list) list.push(i);
      else map.set(i.line_id, [i]);
    }
    return map;
  }, [issues]);

  const kfoGateIssue = useMemo(
    () => issues.find((i) => i.anchor === 'kfo_aosr_gate' && i.decision !== 'accepted') ?? null,
    [issues],
  );
  const ambiguousTotal = allLines.filter((l) => l.match_status === 'ambiguous').length;

  useEffect(() => {
    if (!selectedId) {
      setPoll(0);
      return;
    }
    const st = detailQ.data?.package.status;
    if (st && PACKAGE_RUNNING.has(st)) {
      setPoll(2500);
      return;
    }
    const left = renderWatchUntil - Date.now();
    if (left <= 0) {
      setPoll(0);
      return;
    }
    setPoll(2500);
    const timer = setTimeout(() => setRenderWatchUntil(0), left);
    return () => clearTimeout(timer);
  }, [selectedId, detailQ.data?.package.status, renderWatchUntil]);

  const rebuildNo = detailQ.data?.package.rebuild_no;
  const pkgStatus = detailQ.data?.package.status;
  const refetchAllLines = allLinesQ.refetch;
  const refetchAllMaterials = allMaterialsQ.refetch;
  useEffect(() => {
    if (!selectedId || rebuildNo === undefined) return;
    const stamp = `${selectedId}#${rebuildNo}#${pkgStatus}`;
    const prev = buildStampRef.current;
    buildStampRef.current = stamp;
    if (prev === null || prev === stamp || !prev.startsWith(`${selectedId}#`)) return;
    void refetchAllLines();
    void refetchAllMaterials();
  }, [selectedId, rebuildNo, pkgStatus, refetchAllLines, refetchAllMaterials]);

  useEffect(() => {
    setActFilter('');
    setStep('setup');
    setRenderWatchUntil(0);
    setRenderQueuedAt(0);
  }, [selectedId]);

  useEffect(() => {
    if (acts.length === 0) {
      setFormActId('');
      return;
    }
    if (!acts.some((a) => a.id === formActId)) setFormActId(acts[0].id);
  }, [acts, formActId]);

  const {
    exporting,
    refreshingForms,
    handleCreate,
    handleAddAct,
    handleBuild,
    handleIssue,
    handleSendDsRequest,
    handleImportFromMto,
    handleImportFromLogist,
    handleUploadDocs,
    handleRender,
    handleRenderAct,
    handleRefreshForms,
    handleDownloadForm,
    handleExport,
    handleDelete,
    handleDecision,
    handleBulkExclude,
    handleBulkDecision,
  } = useKsPackageActions({
    pkg,
    selectedId,
    setSelectedId,
    lines,
    allLines,
    issues,
    acts,
    frozen,
    accessToken,
    setNotice,
    setCreateError,
    setActError,
    setDocError,
    setRenderWatchUntil,
    setRenderQueuedAt,
    createPackage,
    deletePackage,
    buildPackage,
    issuePackage,
    createRequest,
    importDocsFromMto,
    importTripsFromLogist,
    upsertAct,
    annotate,
    uploadDocs,
    renderForms,
    renderActForms,
    upsertOverride,
    formsQRefetch: formsQ.refetch,
    detailQRefetch: detailQ.refetch,
  });

  function stepDone(key: StepKey): boolean {
    if (!pkg) return false;
    switch (key) {
      case 'setup':
        return acts.length > 0;
      case 'build':
        return pkg.rebuild_no > 0 && pkg.status !== 'build_failed';
      case 'lines':
        return allLines.length > 0 && ambiguousTotal === 0;
      case 'materials':
        return materialsTotal > 0;
      case 'issues':
        return (
          pkg.rebuild_no > 0 &&
          pkg.status !== 'build_failed' &&
          Boolean(counts && counts.blocking === 0)
        );
      case 'forms':
        return forms.length > 0;
      default:
        return false;
    }
  }

  function stepBadge(
    key: StepKey,
  ): { tone: 'neutral' | 'warning' | 'danger'; text: string } | null {
    if (!pkg) return null;
    switch (key) {
      case 'setup':
        return { tone: acts.length ? 'neutral' : 'warning', text: `актов ${acts.length}` };
      case 'lines':
        return ambiguousTotal > 0
          ? { tone: 'warning', text: `неоднозначных ${ambiguousTotal}` }
          : { tone: 'neutral', text: `${allLines.length}` };
      case 'materials':
        return { tone: 'neutral', text: `${materialsTotal}` };
      case 'issues':
        return counts && counts.blocking > 0
          ? { tone: 'danger', text: `без решения ${counts.blocking}` }
          : { tone: 'neutral', text: `${issues.length}` };
      case 'forms':
        return { tone: 'neutral', text: `${forms.length}` };
      default:
        return null;
    }
  }

  return {
    disabled,
    notice,
    setNotice,
    createError,
    actError,
    docError,
    can_write,
    activeProjectId,
    setActiveProject,
    packages,
    packagesPage,
    packagesHasMore: listQ.data?.has_more,
    packagesFetching: listQ.isFetching,
    creating,
    contextContracts: contextOptionsQ.data?.contracts ?? [],
    contextProjects: contextOptionsQ.data?.projects ?? [],
    estimateOptions: optionsQ.data?.items ?? [],
    selectedId,
    setSelectedId,
    detail,
    pkg,
    acts,
    lines,
    issuesByLine,
    contractLsrs,
    docs,
    materials,
    materialSummary,
    issues,
    counts,
    forms,
    formsError: formsQ.error,
    formsLoading: formsQ.isLoading,
    refreshingForms,
    formActId,
    setFormActId,
    formCode,
    setFormCode,
    exporting,
    detailQError: detailQ.error,
    allLines,
    step,
    setStep,
    actFilter,
    setActFilter,
    frozen,
    busy,
    canDelete,
    kfoGateIssue,
    building,
    issuing,
    sendingRequest,
    importingMto,
    importingLogist,
    uploadingDocs,
    rendering,
    renderingAct,
    savingAct,
    annotating,
    renderQueuedAt,
    renderWatchUntil,
    handleCreate,
    handleAddAct,
    handleBuild,
    handleIssue,
    handleSendDsRequest,
    handleImportFromMto,
    handleImportFromLogist,
    handleUploadDocs,
    handleRender,
    handleRenderAct,
    handleRefreshForms,
    handleDownloadForm,
    handleExport,
    handleDelete,
    handleDecision,
    handleBulkExclude,
    handleBulkDecision,
    stepDone,
    stepBadge,
  };
}
